import type { ApiEndpointsController } from '../../hooks/useApiEndpoints';
import type { DashboardController } from '../../hooks/useDashboard';
import { StatCard } from '../../components/ui/Card';
import { ErrorCard, LoadingCard } from '../../components/ui/ScreenFallback';
import type { ScreenId } from '../../components/layout/navigation';
import { operatorDot } from '../../lib/operators';
import { ApiListCard } from './ApiListCard';
import { ServiceRunsCard } from './ServiceRunsCard';

interface DashboardScreenProps {
  controller: DashboardController;
  /** As APIs vêm do mesmo controller que alimenta o cabeçalho do shell. */
  apis: ApiEndpointsController;
  onNavigate: (screen: ScreenId) => void;
}

/**
 * Tela inicial: quantos masters e quantas linhas ativas existem em cada
 * operadora, o estado das APIs e a última execução de cada serviço. Tudo em
 * cartão, sem tabela — é uma tela de olhada rápida, não de trabalho.
 */
export function DashboardScreen({ controller, apis, onNavigate }: DashboardScreenProps) {
  const { state, loading, error } = controller;

  if (loading && !state) {
    return <LoadingCard message="Carregando o dashboard…" />;
  }

  if (error || !state) {
    return (
      <ErrorCard
        title="Não foi possível carregar o dashboard."
        detail={error}
        onRetry={() => void controller.reload()}
      />
    );
  }

  // Só a API monitorada (ativa) que caiu vira alerta: uma pausada estar fora do
  // ar é esperado, ninguém está olhando para ela.
  const down = (apis.state?.apis ?? []).filter(
    (api) => api.active && api.health.status === 'down'
  );

  return (
    <>
      {down.length > 0 ? (
        <div className="border-danger-200 bg-danger-100 mb-4 flex items-start gap-2.5 rounded-xl border px-3.5 py-3">
          <span className="bg-danger-600 mt-[5px] size-2.5 shrink-0 rounded-full" />
          <div className="text-[13px] leading-normal text-[#8A1F1F]">
            <b>{down.length === 1 ? 'Dependência indisponível:' : 'Dependências indisponíveis:'}</b>{' '}
            {down.map((api) => api.name).join(', ')}{' '}
            <button
              type="button"
              onClick={() => onNavigate('status')}
              className="text-[13px] font-extrabold text-[#8A1F1F] underline"
            >
              Ver status →
            </button>
          </div>
        </div>
      ) : null}

      <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-3">
        {state.masters.map((master) => (
          <StatCard
            key={`master-${master.code}`}
            label={`Masters ${master.name}`}
            value={master.active}
            dot={operatorDot(master.code)}
            sub={
              master.total > master.active
                ? `${master.accounts} ${master.accounts === 1 ? 'conta' : 'contas'} · ${master.total - master.active} ${master.total - master.active === 1 ? 'inativo' : 'inativos'}`
                : `${master.accounts} ${master.accounts === 1 ? 'conta vinculada' : 'contas vinculadas'}`
            }
          />
        ))}

        {state.lines.map((line) => (
          <StatCard
            key={`lines-${line.code}`}
            label={`Linhas ativas ${line.name}`}
            value={line.active}
            dot={operatorDot(line.code)}
            sub={
              line.total > line.active
                ? `${line.total - line.active} ${line.total - line.active === 1 ? 'inativa' : 'inativas'} de ${line.total}`
                : `${line.total} na operadora`
            }
          />
        ))}
      </div>

      <div className="mt-3 grid grid-cols-[repeat(auto-fit,minmax(280px,1fr))] gap-3">
        <ApiListCard apis={apis.state?.apis ?? []} onSeeAll={() => onNavigate('status')} />
        <ServiceRunsCard runs={state.serviceRuns} onSeeAll={() => onNavigate('jobs')} />
      </div>
    </>
  );
}
